// Diagnostics (DESIGN §2): the tempo curve, tap residuals, the half-beat decision, clave evidence, latency and calibration, tracker
// version, storage used, the log export and a plain-text report to copy. Charts are inline SVG; no library.

import { useEffect, useState } from 'preact/hooks';
import { autoRows, claveText, formatReport, halfBeatText, plainMs, residualStats, signedMs, sizeWords, tempoSummary, type AutoReport, type Residual } from '../diagnostics.js';
import { engine } from '../engine/engine.js';
import { useEngineState } from './engine-state.js';
import { exportLog } from './files.js';
import { listSongs, spaceUsed } from './library.js';
import { Sheet } from './sheet.js';
import { showToast } from './ui.js';

const W = 320;
const H = 120;
const PAD = { l: 40, r: 8, t: 8, b: 20 };

/** The local tempo as BPM over song time. */
export function TempoChart({ times, bpm }: { times: readonly number[]; bpm: readonly number[] }) {
  const s = tempoSummary({ times, bpm });
  if (!s) return <p class="note" data-testid="tempo-chart-empty">The tempo curve appears once two taps have been made and the count is locked.</p>;
  const t0 = times[0]!;
  const t1 = times[times.length - 1]! || t0 + 1;
  const pad = Math.max(2, (s.max - s.min) * 0.15);
  const lo = s.min - pad;
  const hi = s.max + pad;
  const x = (t: number) => PAD.l + ((t - t0) / (t1 - t0 || 1)) * (W - PAD.l - PAD.r);
  const y = (b: number) => PAD.t + (1 - (b - lo) / (hi - lo)) * (H - PAD.t - PAD.b);
  const points = times.map((t, i) => `${x(t).toFixed(1)},${y(bpm[i]!).toFixed(1)}`).join(' ');
  return (
    <svg class="chart" data-testid="tempo-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Tempo over the song: from ${s.first.toFixed(0)} to ${s.last.toFixed(0)} beats per minute, between ${s.min.toFixed(0)} and ${s.max.toFixed(0)}.`}>
      <line class="axis" x1={PAD.l} y1={y(lo)} x2={W - PAD.r} y2={y(lo)} />
      <line class="axis" x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={y(lo)} />
      <text class="tick-label" x={PAD.l - 4} y={y(s.max) + 4} text-anchor="end">{s.max.toFixed(0)}</text>
      <text class="tick-label" x={PAD.l - 4} y={y(s.min) + 4} text-anchor="end">{s.min.toFixed(0)}</text>
      <text class="tick-label" x={PAD.l} y={H - 4}>0:00</text>
      <text class="tick-label" x={W - PAD.r} y={H - 4} text-anchor="end">{Math.floor(t1 / 60)}:{String(Math.floor(t1 % 60)).padStart(2, '0')}</text>
      <polyline class="curve" points={points} fill="none" />
    </svg>
  );
}

/** Each tap's distance from the beat, in ms: a dot per tap, zero in the middle. */
export function ResidualChart({ residuals }: { residuals: readonly Residual[] }) {
  const stats = residualStats(residuals);
  if (!stats) return <p class="note" data-testid="residuals-empty">Tap residuals appear once the count is locked.</p>;
  const limit = Math.max(50, Math.ceil(Math.max(...residuals.map((r) => Math.abs(r.ms))) / 25) * 25);
  const n = residuals.length;
  const x = (i: number) => PAD.l + ((i + 0.5) / n) * (W - PAD.l - PAD.r);
  const y = (ms: number) => PAD.t + (1 - (ms + limit) / (2 * limit)) * (H - PAD.t - PAD.b);
  return (
    <>
      <svg class="chart" data-testid="residual-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${n} taps against the grid, between ${signedMs(-limit)} and ${signedMs(limit)}. Mean ${signedMs(stats.mean)}.`}>
        <line class="axis zero" x1={PAD.l} y1={y(0)} x2={W - PAD.r} y2={y(0)} />
        <line class="axis" x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={H - PAD.b} />
        <text class="tick-label" x={PAD.l - 4} y={y(limit) + 4} text-anchor="end">+{limit}</text>
        <text class="tick-label" x={PAD.l - 4} y={y(0) + 4} text-anchor="end">0</text>
        <text class="tick-label" x={PAD.l - 4} y={y(-limit) + 4} text-anchor="end">-{limit}</text>
        <text class="tick-label" x={W - PAD.r} y={H - 4} text-anchor="end">ms, tap by tap</text>
        {residuals.map((r, i) => (
          <circle key={i} class="dot" cx={x(i)} cy={y(r.ms)} r="4"><title>{`Tap ${i + 1}: ${signedMs(r.ms)}`}</title></circle>
        ))}
      </svg>
      <p class="readout-small" data-testid="residual-text">
        {n} {n === 1 ? 'tap' : 'taps'}: mean {signedMs(stats.mean)}, spread {stats.meanAbs.toFixed(1)} ms, worst {signedMs(stats.worst)}.
      </p>
      <p class="note" data-testid="residual-list">{residuals.map((r) => r.ms.toFixed(0)).join(' · ')} ms</p>
    </>
  );
}

export function DiagnosticsSheet() {
  const s = useEngineState();
  const [storage, setStorage] = useState('…');
  const [songsKept, setSongsKept] = useState('none');
  const [report, setReport] = useState<string | null>(null);
  useEffect(() => {
    navigator.storage?.estimate?.().then(
      (e) => setStorage(e.usage !== undefined ? `${sizeWords(e.usage)}${e.quota ? ` of ${sizeWords(e.quota)}` : ''}` : 'not reported'),
      () => setStorage('not reported'),
    ) ?? setStorage('not reported');
    void listSongs().then((songs) => {
      const kept = songs.filter((x) => x.kept);
      if (kept.length) setSongsKept(`${kept.length} ${kept.length === 1 ? 'song' : 'songs'}, ${sizeWords(spaceUsed(kept))}`);
    });
  }, []);

  const info = engine.trackerInfo;
  const { tempoCurve, tapResiduals } = engine.diagnostics;
  const frame = engine.frame();
  const hb = info.halfBeat;
  const auto: AutoReport | undefined = s.source === 'file' ? { run: s.auto.run, declined: s.auto.declined, origin: s.auto.origin, level: s.auto.level, ms: s.auto.ms, proposal: s.auto.proposal } : undefined;

  const rows: [string, string][] = [
    ['Tempo', s.tempoBpm ? `${s.tempoBpm.toFixed(1)} BPM mean${frame.bpm ? `, ${frame.bpm.toFixed(1)} BPM here` : ''}` : 'no count yet'],
    ['Half-beat decision', halfBeatText(hb)],
    ...(auto ? autoRows(auto) : []),
    ['Clave evidence', claveText(s.clave)],
    ['Taps in force', String(s.taps)],
    ['Events in the log', String(s.events.length)],
    ['Output latency', engine.latencyWords],
    ['Output device', engine.deviceKey],
    ['Audio held by this tab', `${sizeWords(engine.memory.heldBytes)} now, most ${sizeWords(engine.memory.peakBytes)} while opening`],
    ['Stretcher residual', plainMs(engine.clock.latency)],
    ['Tap offset', s.calibrationOffsetS === null ? 'not calibrated' : `${plainMs(s.calibrationOffsetS)}${s.calibrationSaved ? '' : ' (memory only)'}`],
    ['Tracker', info.version],
    ['Storage used', storage],
    ['Songs kept on this device', songsKept],
    ['Screen kept awake', s.wakeLock ? 'yes' : 'no'],
  ];

  function makeReport(): string {
    return formatReport({
      generatedAt: new Date().toISOString(),
      track: s.track ? { name: s.track.name, id: s.track.id, duration: s.track.duration } : null,
      source: s.source,
      status: s.status,
      tempoBpm: s.tempoBpm,
      tempoHereBpm: frame.bpm || null,
      taps: s.taps,
      events: s.events.length,
      halfBeat: hb,
      clave: { setting: s.clave.setting, evidence: s.clave.evidence },
      residuals: tapResiduals,
      tempoCurve,
      outputLatencyS: engine.outputLatency,
      stretcherS: engine.clock.latency,
      calibrationOffsetS: s.calibrationOffsetS,
      calibrationSaved: s.calibrationSaved,
      trackerVersion: info.version,
      storage,
      songsKept,
      userAgent: navigator.userAgent,
      ...(auto ? { auto } : {}),
    });
  }

  async function copyReport() {
    const text = makeReport();
    try {
      await navigator.clipboard.writeText(text);
      setReport(null);
      showToast('Report copied');
    } catch {
      setReport(text); // the clipboard is not allowed here: show the text to copy by hand
    }
  }

  return (
    <Sheet title="Diagnostics">
      <section class="diag" aria-labelledby="diag-tempo">
        <h3 id="diag-tempo">Local tempo</h3>
        <TempoChart times={tempoCurve.times} bpm={tempoCurve.bpm} />
      </section>
      <section class="diag" aria-labelledby="diag-resid">
        <h3 id="diag-resid">Taps against the grid</h3>
        <ResidualChart residuals={tapResiduals} />
      </section>
      <dl class="facts" data-testid="diag-facts">
        {rows.map(([k, v]) => (
          <div key={k} class="fact">
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      <button type="button" class="btn" data-testid="copy-report" onClick={() => void copyReport()}>Copy report</button>
      {report !== null && <textarea class="report" readOnly rows={8} aria-label="Report" data-testid="report-text" value={report} onFocus={(e) => e.currentTarget.select()} />}
      <button type="button" class="btn" disabled={s.source !== 'file'} onClick={() => void exportLog()}>Export the log</button>
    </Sheet>
  );
}
